import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, types } from 'mobx-state-tree';

import {
	IGetNotificationsQuery,
	getCounter,
	getNotifications,
	readAllNotification,
} from '@common/api/notifications';

import NotificationModel from './Notification';

const Notifications = types
	.model('Notifications', {
		data: types.optional(types.array(NotificationModel), []),
		limit: types.optional(types.number, 10),
		page: types.optional(types.number, 1),
	})
	.volatile(() => ({
		loading: false,
		hasMore: true,
		unreadCount: 0,
	}))
	.views((self) => ({
		get sortedNotifications() {
			return self.data.slice().sort((a, b) => {
				if (a.isReaded !== b.isReaded) {
					return a.isReaded ? 1 : -1;
				}

				return (
					DateTime.fromISO(b.created_at).toMillis() - DateTime.fromISO(a.created_at).toMillis()
				);
			});
		},
	}))
	.actions((self) => ({
		setPage(page: number) {
			self.page = page;
		},
		setUnreadCount(count: number) {
			self.unreadCount = count;
		},
		decreaseUnreadCount() {
			self.unreadCount -= 1;
		},
	}))
	.actions((self) => ({
		fetch: flow(function* (query: IGetNotificationsQuery) {
			self.loading = true;
			try {
				if (query?.is_modal !== 1) {
					try {
						const count = yield getCounter({ service_id: query?.service_id });
						self.unreadCount = count;
					} catch (e) {
						// ignore
					}
				}

				const result = yield getNotifications({
					page: self.page,
					...query,
				});
				self.hasMore = !!result.next_page_url;
				if (self.page === 1) {
					applySnapshot(self.data, result.data);
				} else {
					self.data.push(...result.data);
				}
			} finally {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		readAll: flow(function* ({
			status,
			query,
		}: {
			status: string;
			query?: IGetNotificationsQuery;
		}) {
			self.loading = true;
			try {
				const result = yield readAllNotification(status);
				if (result?.success) {
					self.setUnreadCount(0);
					self.setPage(1);
					yield self.fetch(query);
				}
			} finally {
				self.loading = false;
			}
		}),
	}));

export default Notifications;

export interface INotificationsModel extends Instance<typeof Notifications> {}
